/**
 * /plyx/explore — single discovery surface that pulls together the
 * existing trending endpoints (hashtags, sounds, featured creators,
 * weekly challenge, live-now ribbon, recent replays) into one
 * scrollable page. Each section just renders the data already shipped
 * by its dedicated backend route — no new server work needed.
 *
 * The page degrades gracefully: every fetch is independent, so a
 * slow/broken endpoint just hides its own section instead of breaking
 * the whole experience.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { plyxApi } from "@/lib/plyxApi";
import {
  Loader2, Hash, Music2, Trophy, Radio, Sparkles, Users, Flame,
} from "lucide-react";

const useSafeFetch = (url) => {
  const [state, setState] = useState({ data: null, loading: true });
  useEffect(() => {
    let mounted = true;
    plyxApi.get(url)
      .then((r) => mounted && setState({ data: r.data, loading: false }))
      .catch(() => mounted && setState({ data: null, loading: false }));
    return () => { mounted = false; };
  }, [url]);
  return state;
};

const PlyxExplore = () => {
  const tags = useSafeFetch("/plyx/tags/trending?limit=10");
  const sounds = useSafeFetch("/plyx/sounds/trending?limit=8");
  const featured = useSafeFetch("/plyx/featured-creators?limit=6");
  const liveNow = useSafeFetch("/plyx/live/active?limit=6");
  const weekly = useSafeFetch("/plyx/challenges/weekly/featured");

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-1">
          <div
            className="w-10 h-10 rounded-2xl flex items-center justify-center text-[#0A0A0A]"
            style={{ background: "linear-gradient(135deg, #FF2D7A, #FFB42D, #00E0C7)" }}
          >
            <Sparkles className="w-5 h-5" />
          </div>
          <h1 className="font-display text-3xl">Explore</h1>
        </div>
        <p className="text-white/55 text-sm mb-8">Everything happening on Plyx right now.</p>

        {/* Live-now ribbon */}
        <Section
          icon={Radio}
          color="#FF2D7A"
          title="Live now"
          empty="Nobody is live this minute."
          loading={liveNow.loading}
          items={liveNow.data?.rooms}
          testid="explore-live-now"
        >
          {(rooms) => (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {rooms.map((r) => (
                <Link
                  key={r.room_name}
                  to={`/plyx/live/${r.room_name}`}
                  data-testid={`explore-live-${r.room_name}`}
                  className="relative aspect-[9/16] rounded-xl overflow-hidden border border-[#FF2D7A]/40 group"
                >
                  {r.thumbnail_url ? (
                    <img src={r.thumbnail_url} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform"/>
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-[#FF2D7A]/30 to-[#0A0A0A]"/>
                  )}
                  <div className="absolute top-2 left-2 inline-flex items-center gap-1 bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"/> LIVE
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/90 to-transparent">
                    <div className="text-xs font-bold text-white truncate">{r.title || r.room_name}</div>
                    <div className="text-[10px] text-white/70 truncate">@{r.creator?.handle}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Section>

        {/* Weekly challenges — the backend ships two cards each week:
            `country_challenge` (Country of the Week) and
            `club_challenge` (Club of the Week). We render whichever
            ones are present. Older responses that put the challenge
            fields at the top level (legacy shape) still work via the
            fallback in `weeklyItems` below. */}
        {(() => {
          const w = weekly.data || {};
          const weeklyItems = [w.country_challenge, w.club_challenge].filter(Boolean);
          // Legacy/fallback: a single challenge object at the top level.
          if (weeklyItems.length === 0 && (w.challenge_id || w.id || w.title)) {
            weeklyItems.push(w);
          }
          return (
            <section data-testid="explore-weekly" className="mb-10">
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Trophy className="w-5 h-5" style={{ color: "#FFB42D" }} />
                  <h2 className="font-bold text-lg text-white">Challenge of the week</h2>
                </div>
                <Link
                  to="/plyx/challenges"
                  data-testid="explore-weekly-see-all"
                  className="text-xs font-semibold text-[#FFB42D] hover:text-white inline-flex items-center gap-1 transition-colors"
                >
                  See all challenges →
                </Link>
              </div>
              {weekly.loading ? (
                <div className="text-white/40 text-sm flex items-center gap-2 py-3">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              ) : weeklyItems.length === 0 ? (
                <Link
                  to="/plyx/challenges"
                  data-testid="explore-weekly-browse-fallback"
                  className="block rounded-2xl p-5 border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] text-center transition-colors"
                >
                  <p className="text-white/55 text-sm">No featured challenge this week.</p>
                  <span className="text-[#FFB42D] text-sm font-semibold mt-1 inline-block">Browse all challenges →</span>
                </Link>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {weeklyItems.map((c) => (
                      <Link
                        key={c.challenge_id || c.id}
                        to={`/plyx/challenges/${c.challenge_id || c.id}`}
                        data-testid={`explore-weekly-card-${c.challenge_id || c.id}`}
                        className="block rounded-2xl p-5 border border-[#FFB42D]/40 bg-gradient-to-br from-[#FFB42D]/10 to-transparent hover:from-[#FFB42D]/20 transition-colors"
                      >
                        <div className="font-display text-2xl text-white">{c.title || c.name}</div>
                        {c.description && <p className="text-white/65 text-sm mt-2 line-clamp-2">{c.description}</p>}
                        <div className="mt-3 flex items-center gap-3 text-xs text-white/55 flex-wrap">
                          <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {c.entry_count ?? c.entries_count ?? c.video_count ?? 0} entries</span>
                          {(c.prize_pool_coins || c.prize_pool) && (
                            <span className="inline-flex items-center gap-1 text-[#FFB42D] font-semibold">
                              {(c.prize_pool_coins || c.prize_pool).toLocaleString()} coins prize
                            </span>
                          )}
                          {c.ends_at && <span>Ends {String(c.ends_at).slice(0, 10)}</span>}
                        </div>
                      </Link>
                    ))}
                  </div>
                  <Link
                    to="/plyx/challenges"
                    data-testid="explore-weekly-browse-all"
                    className="mt-3 block rounded-2xl px-5 py-4 border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] text-center transition-colors group"
                  >
                    <span className="text-sm font-semibold text-white/80 group-hover:text-white">
                      Explore every Plyx challenge →
                    </span>
                    <p className="text-[11px] text-white/45 mt-0.5">Talent shows, country derbies, club battles, dance & comedy.</p>
                  </Link>
                </>
              )}
            </section>
          );
        })()}

        {/* Featured creators */}
        <Section
          icon={Flame}
          color="#00E0C7"
          title="Plyx makers · spotlight"
          loading={featured.loading}
          items={featured.data?.creators}
          empty="No featured creators right now."
          testid="explore-makers"
        >
          {(creators) => (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
              {creators.map((c) => (
                <Link
                  key={c.plyx_user_id || c.handle}
                  to={`/plyx/u/${c.handle}`}
                  data-testid={`explore-maker-${c.handle}`}
                  className="flex flex-col items-center text-center group"
                >
                  <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-[#00E0C7]/40 group-hover:border-[#00E0C7] transition-colors">
                    {c.avatar_url ? (
                      <img src={c.avatar_url} alt="" className="w-full h-full object-cover"/>
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#00E0C7]/40 to-[#FF2D7A]/40 flex items-center justify-center font-display text-xl">
                        {(c.display_name || c.handle || "?")[0]?.toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="text-xs font-bold text-white mt-1.5 truncate w-full">@{c.handle}</div>
                  {typeof c.follower_count === "number" && (
                    <div className="text-[10px] text-white/55">{c.follower_count} fans</div>
                  )}
                </Link>
              ))}
            </div>
          )}
        </Section>

        {/* Trending hashtags */}
        <Section
          icon={Hash}
          color="#FF2D7A"
          title="Trending hashtags"
          loading={tags.loading}
          items={tags.data?.tags}
          empty="No trending tags yet."
          testid="explore-tags"
        >
          {(items) => (
            <div className="flex flex-wrap gap-2">
              {items.map((t) => (
                <Link
                  key={t.slug || t.tag}
                  to={`/plyx/tag/${t.slug || t.tag}`}
                  data-testid={`explore-tag-${t.slug || t.tag}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.04] hover:bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7]/15 border border-white/10 hover:border-[#FF2D7A]/40 transition-colors"
                >
                  <Hash className="w-3.5 h-3.5 text-[#FF2D7A]" />
                  <span className="text-sm font-semibold text-white">{t.tag || t.slug}</span>
                  <span className="text-[10px] text-white/45">{t.video_count || t.count || 0}</span>
                </Link>
              ))}
            </div>
          )}
        </Section>

        {/* Trending sounds */}
        <Section
          icon={Music2}
          color="#FFB42D"
          title="Trending sounds"
          loading={sounds.loading}
          items={sounds.data?.sounds}
          empty="No trending sounds yet."
          testid="explore-sounds"
        >
          {(items) => (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {items.map((s) => (
                <Link
                  key={s.sound_id}
                  to={`/plyx/sound/${s.sound_id}`}
                  data-testid={`explore-sound-${s.sound_id}`}
                  className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/10"
                >
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#FFB42D] to-[#FF2D7A] flex items-center justify-center shrink-0">
                    <Music2 className="w-4 h-4 text-[#0A0A0A]"/>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-white truncate">{s.title || s.name || "Untitled"}</div>
                    <div className="text-[11px] text-white/55 truncate">
                      {s.artist || s.creator?.handle ? `${s.artist || `@${s.creator?.handle}`} · ` : ""}
                      {s.use_count || s.video_count || 0} uses
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
};

const Section = ({ icon: Icon, color, title, items, loading, empty, children, testid }) => {
  const hasItems = Array.isArray(items) && items.length > 0;
  if (!loading && !hasItems) return null;
  return (
    <section data-testid={testid} className="mb-10">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-5 h-5" style={{ color }} />
        <h2 className="font-bold text-lg text-white">{title}</h2>
      </div>
      {loading ? (
        <div className="text-white/40 text-sm flex items-center gap-2 py-3">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : !hasItems ? (
        <p className="text-white/40 text-sm">{empty}</p>
      ) : (
        children(items)
      )}
    </section>
  );
};

export default PlyxExplore;
